import { Badge } from '@/components/ui/badge';
import { ROLE_METADATA, UserRole } from '@/components/authnz/types/rbac';
import type { GrantChip } from '@/lib/users-roles/types';
import { cn } from '@/lib/utils';
import { X } from 'lucide-react';
import { RoleSwatch } from './RoleSwatch';

export function GrantChips({
  grants,
  onRevokeAdmin,
  revokeAlwaysVisible = false,
}: {
  grants: GrantChip[];
  onRevokeAdmin?: (role: UserRole) => void;
  revokeAlwaysVisible?: boolean;
}) {
  if (grants.length === 0) {
    return <span className="text-muted-foreground text-xs">No grants</span>;
  }

  return (
    <div className="flex flex-wrap gap-1.5 items-center min-w-0">
      {grants.map((grant) => (
        <Badge
          key={`${grant.role}-${grant.source}`}
          variant={grant.source === 'subscription' ? 'secondary' : 'outline'}
          className="gap-1 relative group/grant"
          data-testid="grant-chip"
        >
          <RoleSwatch role={grant.role} />
          {ROLE_METADATA[grant.role]?.displayName ?? grant.role}
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
            {grant.source}
          </span>
          {!grant.readOnly && grant.source !== 'subscription' && onRevokeAdmin ? (
            <button
              type="button"
              data-testid={`remove-role-${grant.role}`}
              className={cn(
                'ml-0.5 transition-opacity',
                revokeAlwaysVisible
                  ? 'opacity-100'
                  : 'opacity-0 group-hover/grant:opacity-100',
              )}
              aria-label={`Remove ${ROLE_METADATA[grant.role]?.displayName ?? grant.role}`}
              onClick={(event) => {
                event.stopPropagation();
                onRevokeAdmin(grant.role);
              }}
            >
              <X className="h-3 w-3" />
            </button>
          ) : grant.readOnly || grant.source === 'subscription' ? (
            <span className="sr-only">managed by subscription</span>
          ) : null}
        </Badge>
      ))}
    </div>
  );
}
